import { svgDataUrl } from "./poster";

/**
 * Export de l'affiche en PNG, entierement dans le navigateur : le SVG (polices encodees
 * dedans) est dessine dans un canvas, puis enregistre. Aucun appel serveur.
 *
 * Deux formats : 1080 x 1350 (4:5, publication) et 1080 x 1920 (9:16, story).
 */

export interface ExportFormat {
  width: number;
  height: number;
  filename: string;
}

export const EXPORTS = {
  post: { width: 1080, height: 1350, filename: "line-up-de-ma-vie.png" },
  story: { width: 1080, height: 1920, filename: "line-up-de-ma-vie-story.png" },
} as const satisfies Record<string, ExportFormat>;

/** Charge une image et attend qu'elle soit decodee. */
export async function loadImage(src: string): Promise<HTMLImageElement> {
  const image = new Image();
  const loaded = new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error("L'image de l'affiche n'a pas pu etre lue."));
  });
  image.src = src;
  await loaded;
  return image;
}

/** Dessine un SVG dans un canvas de la taille demandee et rend le PNG. */
export async function svgToPng(svg: string, width: number, height: number): Promise<Blob> {
  const image = await loadImage(svgDataUrl(svg));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Ce navigateur ne sait pas dessiner l'affiche.");
  }
  context.drawImage(image, 0, 0, width, height);
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error("Le PNG n'a pas pu etre produit."));
      }
    }, "image/png");
  });
}

/** Propose un fichier a l'enregistrement. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Exporte un SVG en PNG et le propose a l'enregistrement. */
export async function exportPoster(svg: string, format: ExportFormat): Promise<void> {
  const blob = await svgToPng(svg, format.width, format.height);
  saveBlob(blob, format.filename);
}
